import { extractTrackSegments, buildEntryForSegment } from '../splitTimes';
import { computeCoachingConfidence, defaultCoachAdvice } from './coachAdvice';
import { average, spread, clamp01, normalizeRange, titleCase, formatMph } from './helpers';

function getLapKey(lap) {
  return lap?.driverLabel ? `${lap.driverLabel}:${lap.lapNum}` : `${lap?.lapNum}`;
}

function buildReferenceMap(laps, segments) {
  const fastest = [...laps]
    .filter((lap) => Number.isFinite(lap?.lapTime))
    .sort((a, b) => a.lapTime - b.lapTime)
    .slice(0, 3);

  const refs = new Map();

  segments.forEach((segment) => {
    const entries = fastest
      .map((lap) => buildEntryForSegment(lap, segment, 200))
      .filter(Boolean);

    refs.set(segment.index, {
      entrySpeedAvgMph: average(entries.map((entry) => entry.entrySpeedMph).filter(Number.isFinite)),
      minSpeedAvgMph: average(entries.map((entry) => entry.minSpeedMph).filter(Number.isFinite)),
      exitSpeedAvgMph: average(entries.map((entry) => entry.exitSpeedMph).filter(Number.isFinite)),
    });
  });

  return refs;
}

function buildSegmentRows({ laps, trackCoordinates }) {
  const segments = extractTrackSegments(trackCoordinates);
  if (!segments.length || !laps?.length) return [];

  const referenceMap = buildReferenceMap(laps, segments);

  return segments.map((segment) => {
    const entries = laps
      .map((lap) => {
        const entry = buildEntryForSegment(lap, segment, 200);
        return entry ? { ...entry, lapTime: lap.lapTime, lapKey: getLapKey(lap) } : null;
      })
      .filter(Boolean);

    if (!entries.length) return null;

    const entrySpeeds = entries.map((entry) => entry.entrySpeedMph).filter(Number.isFinite);
    const minSpeeds = entries.map((entry) => entry.minSpeedMph).filter(Number.isFinite);
    const exitSpeeds = entries.map((entry) => entry.exitSpeedMph).filter(Number.isFinite);
    const reference = referenceMap.get(segment.index) || null;

    const row = {
      segmentIndex: segment.index,
      cornerId: segment.label,
      label: segment.label,
      sourceUsed: 'derived',
      eventCount: entries.length,
      avgEntrySpeedMph: average(entrySpeeds),
      avgMinSpeedMph: average(minSpeeds),
      avgExitSpeedMph: average(exitSpeeds),
      entrySpeedSpreadMph: spread(entrySpeeds),
      minSpeedSpreadMph: spread(minSpeeds),
      exitSpeedSpreadMph: spread(exitSpeeds),
      reference,
      referenceDelta: reference ? {
        entrySpeedDeltaMph: (average(entrySpeeds) ?? 0) - (reference.entrySpeedAvgMph ?? 0),
        minSpeedDeltaMph: (average(minSpeeds) ?? 0) - (reference.minSpeedAvgMph ?? 0),
        exitSpeedDeltaMph: (average(exitSpeeds) ?? 0) - (reference.exitSpeedAvgMph ?? 0),
      } : null,
    };

    const entryPenalty = normalizeRange(row.entrySpeedSpreadMph, 3, 8) * 0.3;
    const minPenalty = normalizeRange(row.minSpeedSpreadMph, 2.5, 7) * 0.4;
    const exitPenalty = normalizeRange(row.exitSpeedSpreadMph, 2.5, 6) * 0.3;
    row.score = Math.max(0, Math.min(100, Math.round(100 - (entryPenalty + minPenalty + exitPenalty) * 100)));

    return row;
  }).filter(Boolean);
}

function classifySegmentSpeedIssue(segment) {
  const minDelta = segment?.referenceDelta?.minSpeedDeltaMph ?? null;
  const exitDelta = segment?.referenceDelta?.exitSpeedDeltaMph ?? null;
  const entrySpread = segment?.entrySpeedSpreadMph ?? 0;
  const minSpread = segment?.minSpeedSpreadMph ?? 0;
  const eventCount = segment?.eventCount ?? 0;

  if (eventCount < 2) {
    return {
      type: 'inconclusive',
      patternStrength: 0.1,
      outcomeCorrelation: 0,
      causalClarity: 0.1,
      signalNoise: 0.45,
    };
  }

  if (minDelta != null && minDelta < -2.2 && (exitDelta == null || exitDelta < -0.8)) {
    return {
      type: 'overslowing_midcorner',
      patternStrength: normalizeRange(Math.abs(minDelta), 1.5, 4),
      outcomeCorrelation: clamp01((normalizeRange(Math.abs(minDelta), 1.5, 4) * 0.7) + 0.2),
      causalClarity: 0.82,
      signalNoise: 0.12,
    };
  }

  if (exitDelta != null && exitDelta < -2) {
    return {
      type: 'weak_exit_speed',
      patternStrength: normalizeRange(Math.abs(exitDelta), 1.5, 4),
      outcomeCorrelation: clamp01((normalizeRange(Math.abs(exitDelta), 1.5, 4) * 0.75) + 0.15),
      causalClarity: 0.76,
      signalNoise: 0.12,
    };
  }

  if (minSpread >= 4.5) {
    return {
      type: 'midcorner_speed_inconsistent',
      patternStrength: normalizeRange(minSpread, 3.5, 8),
      outcomeCorrelation: 0.45,
      causalClarity: 0.74,
      signalNoise: 0.14,
    };
  }

  if (entrySpread >= 5.5) {
    return {
      type: 'entry_speed_inconsistent',
      patternStrength: normalizeRange(entrySpread, 4.5, 9),
      outcomeCorrelation: 0.38,
      causalClarity: 0.68,
      signalNoise: 0.14,
    };
  }

  if ((segment?.score ?? 0) >= 88) {
    return {
      type: 'stable',
      patternStrength: normalizeRange(segment.score, 88, 98),
      outcomeCorrelation: 0.42,
      causalClarity: 0.7,
      signalNoise: 0.08,
    };
  }

  return {
    type: 'mild_variation',
    patternStrength: 0.35,
    outcomeCorrelation: 0.25,
    causalClarity: 0.45,
    signalNoise: 0.16,
  };
}

function buildObservation(segment, diagnosis, user) {
  const delta = segment?.referenceDelta || null;
  const eventNote = segment?.eventCount ? ` across ${segment.eventCount} laps` : '';
  const fm = (v) => formatMph(v, user);

  switch (diagnosis.type) {
    case 'overslowing_midcorner': {
      let obs = `Average minimum speed in ${segment.label} is ${fm(delta?.minSpeedDeltaMph)} below your best 3 laps${eventNote}.`;
      if (segment.avgMinSpeedMph != null) {
        obs += ` Your average minimum speed is ${fm(segment.avgMinSpeedMph)} versus ${fm(segment.reference?.minSpeedAvgMph)} in your fastest laps.`;
      }
      if (delta?.exitSpeedDeltaMph != null && delta.exitSpeedDeltaMph < -0.8) {
        obs += ` Exit speed is also down by ${fm(delta.exitSpeedDeltaMph)}, carrying the mid-corner loss onto the following straight.`;
      }
      return obs;
    }
    case 'weak_exit_speed': {
      let obs = `Exit speed out of ${segment.label} is ${fm(delta?.exitSpeedDeltaMph)} below your best 3 laps${eventNote}.`;
      if (segment.avgExitSpeedMph != null) {
        obs += ` Your average exit is ${fm(segment.avgExitSpeedMph)} versus ${fm(segment.reference?.exitSpeedAvgMph)} at your best.`;
      }
      if (delta?.entrySpeedDeltaMph != null && Math.abs(delta.entrySpeedDeltaMph) < 1.5) {
        obs += ' Entry speed is close to the reference, so the loss is happening in the second half of the section.';
      }
      return obs;
    }
    case 'midcorner_speed_inconsistent':
      return `Minimum-speed spread is ${fm(segment.minSpeedSpreadMph)} in ${segment.label}${eventNote}. ${segment.avgMinSpeedMph != null ? `Average minimum speed: ${fm(segment.avgMinSpeedMph)}.` : ''} Entry-speed spread is ${fm(segment.entrySpeedSpreadMph)} and exit-speed spread is ${fm(segment.exitSpeedSpreadMph)}, so the mid-corner is the least consistent phase.`;
    case 'entry_speed_inconsistent':
      return `Entry-speed spread is ${fm(segment.entrySpeedSpreadMph)} in ${segment.label}${eventNote}. ${segment.avgEntrySpeedMph != null ? `Average entry speed: ${fm(segment.avgEntrySpeedMph)}.` : ''} You are arriving at this section at a different speed each lap, which cascades through the entire corner. Minimum-speed spread is ${fm(segment.minSpeedSpreadMph)} and exit-speed spread is ${fm(segment.exitSpeedSpreadMph)}.`;
    case 'stable':
      return `${segment.label} holds a repeatable speed profile${eventNote}: entry spread is ${fm(segment.entrySpeedSpreadMph)}, minimum-speed spread is ${fm(segment.minSpeedSpreadMph)}, exit spread is ${fm(segment.exitSpeedSpreadMph)}. ${segment.score != null ? `Consistency score: ${segment.score}/100.` : ''} All three phases are well-calibrated.`;
    case 'inconclusive':
      return `Only ${segment.eventCount || 'a few'} clean passes recorded in ${segment.label}. There is not enough data to identify a reliable speed pattern yet.`;
    default:
      return `${segment.label} shows some speed variation${eventNote} — entry spread: ${fm(segment.entrySpeedSpreadMph)}, minimum-speed spread: ${fm(segment.minSpeedSpreadMph)}, exit spread: ${fm(segment.exitSpeedSpreadMph)} — but no single speed issue clearly dominates the pattern yet.`;
  }
}

function buildInference(diagnosis) {
  switch (diagnosis.type) {
    case 'overslowing_midcorner':
      return 'You are losing speed in the middle of this section, which compounds through the exit and onto the following straight. Mid-corner speed loss usually comes from one of three causes: trailing the brake too deep into the turn, taking a tighter line than needed, or lifting off the throttle mid-corner. The data suggests you have more grip available than you are using at the apex. Even 1-2 mph more minimum speed translates to a meaningful gain because it carries through the entire exit phase.';
    case 'weak_exit_speed':
      return 'The entry looks closer to the mark, so the time loss is concentrated in the second half of this section. Weak exit speed is usually caused by late throttle application, premature throttle (which causes a correction), or too much steering lock at the exit. The exit phase has an outsized impact on lap time because every mph gained at the exit carries all the way down the next straight. Fixing exit speed is often the highest-value change you can make in any section.';
    case 'midcorner_speed_inconsistent':
      return 'The center of this section is the least repeatable phase, which means the line or the balance between braking and turning is changing lap to lap. When minimum speed varies this much, it is hard to build a consistent exit, which makes throttle application unpredictable. Inconsistent mid-corner speed is often a vision issue — if your eyes are not consistently on the apex reference, the car will take a different arc each time.';
    case 'entry_speed_inconsistent':
      return 'A wide entry-speed spread means the entire corner starts from a different place each lap. Entry speed is usually set by what happens upstream — the braking zone, the previous corner exit, or both. When entry speed is inconsistent, everything downstream (minimum speed, line, exit) becomes unpredictable, making it impossible to tune any single technique variable. Stabilizing entry speed is the foundation for consistent section times.';
    case 'stable':
      return 'Entry, minimum, and exit speeds are all tightly grouped, which means your line, braking, and throttle inputs are well-calibrated through this section. A consistent speed profile like this is the hallmark of a well-driven corner. Use the feel and process you have here as a reference when working on less consistent sections elsewhere on the lap.';
    default:
      return 'The speed data shows variation across the section, but the pattern does not clearly point to one phase (entry, mid-corner, or exit) as the dominant problem. This can happen when multiple small variations overlap. More clean laps will help separate the signals and reveal which phase to focus on first.';
  }
}

function buildTitle(diagnosis, segment) {
  switch (diagnosis.type) {
    case 'overslowing_midcorner':
      return `${segment.label} is over-slowed mid-corner`;
    case 'weak_exit_speed':
      return `${segment.label} is losing speed on exit`;
    case 'midcorner_speed_inconsistent':
      return `${segment.label} is inconsistent through the center`;
    case 'entry_speed_inconsistent':
      return `${segment.label} arrives with mixed entry speeds`;
    case 'stable':
      return `${segment.label} is a segment-speed benchmark`;
    case 'inconclusive':
      return `${segment.label} needs more segment-speed data`;
    default:
      return `${segment.label} has mild speed variation`;
  }
}

function buildType(diagnosis) {
  if (diagnosis.type === 'stable') return 'positive';
  if (diagnosis.type === 'inconclusive') return 'neutral';
  return 'warning';
}

export function buildSegmentSpeedCoachCards({
  laps,
  trackCoordinates,
  user,
}) {
  const segments = buildSegmentRows({ laps, trackCoordinates });
  if (!segments.length) return [];

  const cards = segments.map((segment) => {
    const diagnosis = classifySegmentSpeedIssue(segment);
    const confidence = computeCoachingConfidence({
      sourceUsed: 'derived',
      eventCount: segment.eventCount,
      patternStrength: diagnosis.patternStrength,
      outcomeCorrelation: diagnosis.outcomeCorrelation,
      causalClarity: diagnosis.causalClarity,
      signalNoise: diagnosis.signalNoise,
    });

    return {
      id: `segment-speed-${segment.segmentIndex}-${diagnosis.type}`,
      cornerId: segment.label,
      title: buildTitle(diagnosis, segment),
      type: buildType(diagnosis),
      diagnosis: diagnosis.type,
      observation: confidence.score >= 35 ? buildObservation(segment, diagnosis, user) : null,
      inference: confidence.score >= 50 ? buildInference(diagnosis) : null,
      recommendation: confidence.score >= 35 ? defaultCoachAdvice.getAdvice({
        domain: 'segmentSpeed',
        diagnosisType: diagnosis.type,
        confidenceLabel: confidence.label,
        segment,
        user,
      }) : null,
      confidence,
      priority: Math.round((diagnosis.patternStrength * 100) + (confidence.score * 0.55)),
      meta: `${titleCase(confidence.label)} confidence`,
      referenceLabel: 'Best 3 laps',
    };
  }).filter((card) => card.confidence.score >= 35);

  const allActionable = cards
    .filter((card) => card.diagnosis !== 'stable' && card.diagnosis !== 'inconclusive')
    .sort((a, b) => b.priority - a.priority);
  const actionable = allActionable.slice(0, 2);

  const benchmark = cards
    .filter((card) => card.diagnosis === 'stable')
    .sort((a, b) => b.confidence.score - a.confidence.score)[0];

  let positiveCard = benchmark;
  if (!positiveCard) {
    const actionableIds = new Set(actionable.map((c) => c.id));
    const candidate = cards
      .filter((card) => !actionableIds.has(card.id) && card.diagnosis !== 'inconclusive')
      .sort((a, b) => b.confidence.score - a.confidence.score)[0];
    if (candidate) {
      positiveCard = {
        ...candidate,
        type: 'positive',
        title: `${candidate.cornerId ? candidate.cornerId : 'Best section'} is your strongest speed section`,
        diagnosis: 'best_available',
        recommendation: defaultCoachAdvice.getAdvice({
          domain: 'segmentSpeed',
          diagnosisType: 'best_available',
          confidenceLabel: candidate.confidence.label,
          segment: { label: candidate.cornerId },
        }),
      };
    }
  }

  return [
    ...actionable,
    ...(positiveCard ? [positiveCard] : []),
  ];
}
